import axios from 'axios'
import { API_URL, API_TIMEOUT } from '../config'
import router from "@/router/index.js";

const api = axios.create({
    baseURL: API_URL,
    timeout: API_TIMEOUT,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
})

// Request interceptor for adding the auth token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token')
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

let isSessionExpiredAlertOpen = false;

// Response interceptor for handling common errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        // Handle 401 Unauthorized errors (token expired)
        if (error.response && error.response.status === 401) {
            if (router.currentRoute.value.path !== '/login') {
                localStorage.removeItem('token');
                if (!isSessionExpiredAlertOpen) {
                    isSessionExpiredAlertOpen = true;
                    import('@/utils/alerts').then(({ showSessionExpired }) => {
                        showSessionExpired().then(() => {
                            isSessionExpiredAlertOpen = false;
                            router.push('/login?session_expired=true');
                        });
                    }).catch(() => {
                        isSessionExpiredAlertOpen = false;
                        router.push('/login?session_expired=true');
                    });
                }
            }
        }

        return Promise.reject(error)
    }
)

// Add this function to set auth header explicitly
export const setAuthHeader = (token) => {
    if (token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
        delete api.defaults.headers.common['Authorization'];
    }
};




export const extractApiErrorMessage = (error, fallback = 'Operation failed') => {
    if (error.response && error.response.data) {
        const data = error.response.data;
        if (data.errors && typeof data.errors === 'object') {
            const errorList = Object.values(data.errors).flat().filter(Boolean);
            if (errorList.length > 0) {
                return errorList.join(' ');
            }
        }
        if (data.message && data.message !== 'The given data was invalid.') {
            return data.message;
        }
        if (data.error) {
            return data.error;
        }
    }
    return error.response?.data?.message || error.message || fallback;
};

const createAuthError = (error, defaultMessage) => {
    const message = extractApiErrorMessage(error, defaultMessage);
    const err = new Error(message);
    if (error.response?.data?.errors) {
        err.errors = error.response.data.errors;
    }
    return err;
};

export const authService = {
    login: async (email, password, recaptchaToken) => {
        try {
            const response = await api.post('/login', { email, password, recaptchaToken });
            return response.data;
        } catch (error) {
            throw createAuthError(error, 'Login failed');
        }
    },

    register: async ({ fullName, email, phone, password, recaptchaToken }) => {
        try {
            const response = await api.post('/register', { fullName, email, phone, password, recaptchaToken });
            return response.data;
        } catch (error) {
            throw createAuthError(error, 'Registration failed');
        }
    },

    logout: async () => {
        try {
            // If your API has a logout endpoint
            await api.post('/logout');

            // Clear token and user data
            localStorage.removeItem('token');
            localStorage.removeItem('user');
        } catch (error) {
            console.error('Logout error:', error);
        }
    },

    forgotPassword: async (email) => {
        try {
            const response = await api.post('/password/forgot', { email });
            return response.data;
        } catch (error) {
            throw createAuthError(error, 'Unable to send reset link.');
        }
    },

    resetPassword: async ({ token, email, password, password_confirmation }) => {
        try {
            const response = await api.post('/password/reset', { token, email, password, password_confirmation });
            return response.data;
        } catch (error) {
            throw createAuthError(error, 'Password reset failed.');
        }
    }
};


export const fetchHomeData = async (timeout = 100000, retries = 2) => {
    console.log('Fetching home data from API...')

    for (let attempt = 0; attempt <= retries; attempt++) {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), timeout)

        try {

            const response = await api.get('/home', {
                signal: controller.signal,
                timeout: timeout
            })

            clearTimeout(timeoutId)
            console.log('Home data fetched successfully')
            return response.data

        } catch (error) {
            clearTimeout(timeoutId)

            if (attempt === retries) {
                console.error(`Failed to fetch home data after ${retries + 1} attempts:`, error)

                // Return fallback data instead of throwing
                return {
                    categories: [],
                    scategories: [],
                    articles: [],
                    ads: [],
                    configurations: [],
                    shouldUseDAPricing: true,
                    country: 'INT',
                    user: null,
                    favoris: []
                }
            }

            // Exponential backoff before retry
            const delay = Math.pow(2, attempt) * 1000
            console.log(`Retry attempt ${attempt + 1} in ${delay}ms...`)
            await new Promise(resolve => setTimeout(resolve, delay))
        }
    }
}


export const fetchSubCategory = async (id) => {
    try {
        // Check if this path matches your Laravel API structure
        const response = await api.get(`/sous_categorie/${id}`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching subcategory ${id}:`, error);
        throw error;
    }
};

export const fetchArticles = async (subCategoryId) => {
    try {

        // Try first endpoint format
        try {
            const response = await api.get(`/sous_categorie/${subCategoryId}/articles`);
            return response.data;
        } catch (error1) {

            // Try second endpoint format
            try {
                const response = await api.get(`/articles/byscid/${subCategoryId}`);
                return response.data;
            } catch (error2) {

                // Try third endpoint format
                try {
                    const response = await api.get(`/articles/${subCategoryId}`);
                    return response.data;
                } catch (error3) {
                    throw new Error('All article endpoints failed');
                }
            }
        }
    } catch (error) {
        console.error(`Error fetching articles for subcategory ${subCategoryId}:`, error);
        throw error;
    }
};

export const updateUserPreferences = async (preferences) => {
    try {
        // Axios expects (url, data, config) not (url, {headers, body})
        const response = await api.post('/user/preferences', preferences, {
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }
        });

        return response.data;
    } catch (error) {
        console.error('API error:', error);
        if (error.response && error.response.data) {
            throw new Error(error.response.data.message || 'Failed to update preferences');
        }
        throw new Error('Failed to update preferences');
    }
};


export const placeDirectOrder = async (orderData) => {
    try {
        // Ensure required fields are present
        if (!orderData.article_id) {
            throw new Error('Article ID is required');
        }

        // Set quantity to 1 if not provided
        if (!orderData.quantity) {
            orderData.quantity = 1;
        }

        // Make API request to orders endpoint
        const response = await api.post('/orders', orderData);

        return response.data;
    } catch (error) {
        console.error('API error:', error);

        if (error.response && error.response.data) {
            throw new Error(error.response.data.error || error.response.data.message || 'Failed to place order');
        }

        throw new Error('Failed to place order. Please try again.');
    }
};

/**
 * Place a bulk order with multiple items
 * @param {Array} items - Array of order items
 * @param {String} paymentMethod - Selected payment method
 * @param {String} paymentNotes - Optional payment notes
 * @param {String} coupon_code - Optional payment notes
 * @param {File} paymentProof - Optional file for payment proof
 * @returns {Promise<Object>} Order confirmation details
 */
export const placeBulkOrder =
    async (items, paymentMethod, paymentNotes = null,
        paymentProof = null, coupon_code = null) => {
        try {
            // Prepare the FormData object for the multipart request
            const formData = new FormData();

            // Append each item as a separate key with indexed notation
            items.forEach((item, index) => {
                for (const key in item) {
                    if (item[key] !== null) {
                        formData.append(`items[${index}][${key}]`, item[key]);
                    }
                }
            });

            // Add payment method and notes as top-level fields
            formData.append('payment_method', paymentMethod);
            if (paymentNotes) {
                formData.append('payment_notes', paymentNotes);
            }

            // Add payment proof if provided
            if (paymentProof) {
                formData.append('payment_proof', paymentProof);
            }

            if (coupon_code) {
                formData.append('coupon_code', coupon_code);
            }

            // Make the API call with the correctly formatted FormData
            const response = await api.post('/bulk-orders', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            return response.data;
        } catch (error) {
            console.error('API error:', error);

            if (error.response && error.response.data) {
                throw new Error(error.response.data.error || error.response.data.message || 'Failed to place order');
            }

            throw new Error('Failed to place order. Please try again.');
        }
    };


// Add this method in your coupon/promo section (or create one if it doesn't exist)
export const validateCoupon = async (code) => {
    try {
        const response = await api.post('/coupons/validate', {
            code: code
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};


export const updateProfile = async (profileData) => {
    try {
        const response = await api.put('/user/profile', profileData, {
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }
        });
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};

/**
 * Update user password
 */
export const updatePassword = async (passwordData) => {
    try {
        const response = await api.put('/user/password', passwordData,
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};

/**
 * Get user orders
 */
export const getUserOrders = async (params = {}) => {
    try {
        const response = await api.get('/user/orders', { params });
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};

/**
 * Cancel user order
 */
export const cancelUserOrder = async (orderId) => {
    try {
        const response = await api.post(`/user/orders/${orderId}/cancel`);
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};

/**
 * Get user transactions
 */
export const getUserTransactions = async (params = {}) => {
    try {
        const response = await api.get('/user/transactions', { params });
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};

/**
 * Get user login history
 */
export const getUserLoginHistory = async (params = {}) => {
    try {
        const response = await api.get('/user/login-history', { params });
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};

export const getUserPaymentHistory = async (params) => {
    try {
        const response = await api.get('/user/payment-history', { params });
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};


/**
 * Report suspicious login
 */
export const reportSuspiciousLogin = async (loginId) => {
    try {
        const response = await api.post(`/user/login-history/${loginId}/report`);
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};

/**
 * Get payment methods for wallet charging
 */
export const getPaymentMethods = async () => {
    try {
        const response = await api.get('/payment-methods/charge');
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};
/**
 * Get V2 Game Articles (FreeFire/PUBG) for a given sub-category
 * @param {number} subcategoryId - The sub-category ID (163=FF, 162=PUBG)
 * @returns {Promise<Object>} Response with articles list
 */
export const getGameArticles = async (subcategoryId) => {
    try {
        const response = await api.get(`/sous_categorie/${subcategoryId}/v2-articles`);
        return response.data;
    } catch (error) {
        console.error('Game articles error:', error);
        throw error;
    }
};

/**
 * Check V2 Player ID (s2t V2)
 * @param {string} playerId - The player ID to validate
 * @param {number|string} itemId - The V2 Category/Item ID (subcategory_id in API)
 * @returns {Promise} Response with player info or error
 */
export const validateV2Player = async (playerId, itemId) => {
    try {
        const payload = {
            player_id: playerId,
            sub_category_id: itemId
        };

        const response = await api.post('/freefire-v2/validate-player', payload);
        return response.data;
    } catch (error) {
        console.error('FreeFire V2 API error:', error);
        if (error.response && error.response.data) {
            return error.response.data;
        }
        throw error;
    }
};

/**
 * Check V2 Order Status from V2 API (User-side)
 * @param {number|string} orderId - The local order ID
 * @returns {Promise<Object>} Response with status info
 */
export const checkV2OrderStatus = async (orderId) => {
    try {
        const response = await api.get(`/freefire-v2/check-status/${orderId}`);
        return response.data;
    } catch (error) {
        console.error('V2 Status Check error:', error);
        if (error.response && error.response.data) {
            return error.response.data;
        }
        throw error;
    }
};

/**
 * Check FreeFire Player ID (Legacy V1)
 * @param {string} playerId - The player ID to validate
 * @returns {Promise} Response with player info or error
 */
export const checkFreeFirePlayer = async (playerId) => {
    try {
        const payload = {
            player_id: playerId
        };

        // This will use the authenticated API instance with token
        const response = await api.post('/freefire/check-player', payload);

        // Return the full response data
        return response.data;

    } catch (error) {
        console.error('FreeFire API error:', error);

        // If backend returns validation error, pass it through
        if (error.response && error.response.data) {
            return error.response.data;
        }

        // For network or other errors
        throw error;
    }
};

/**
 * Check Internet Number (ADSL or 4G LTE)
 * @param {string} type - The internet type ('ADSL' or '4G')
 * @param {string} number - The line number to validate
 * @returns {Promise<Object>} Response with validation info
 */
export const sendContactMessage = async (data) => {
    try {
        const response = await api.post('/contact', data);
        return response.data;
    } catch (error) {
        if (error.response) {
            throw new Error(error.response.data.message || 'Failed to send message.');
        }
        throw new Error('Network error occurred');
    }
};

export const checkInternetNumber = async (type, number) => {
    try {
        const payload = {
            type: type,
            number: number
        };

        const response = await api.post('internet/check-number', payload);

        return response.data;

    } catch (error) {
        console.error('Internet Validation API error:', error);
        if (error.response && error.response.data) {
            // Priority to specific error message (e.g. 24h limit) over generic wrapper
            return error.response.data;
        }
        throw error;
    }
};

/**
 * Submit wallet charge request
 */
export const createChargeRequest = async (formData) => {
    try {
        const response = await api.post('/user/charge-wallet', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};




/**
 * Get messages for an order (user side)
 */
export const getOrderMessages = async (commandeId, params = {}) => {
    try {
        const response = await api.get(`/commandes/${commandeId}/messages`, { params });
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};

/**
 * Send a message for an order (user side)
 */
export const sendOrderMessage = async (commandeId, message) => {
    try {
        const response = await api.post(`/commandes/${commandeId}/messages`, { message });
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};

/**
 * Mark a message as read (user side)
 */
export const markMessageRead = async (commandeId, messageId) => {
    try {
        const response = await api.patch(`/commandes/${commandeId}/messages/${messageId}/read`);
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};

/**
 * Mark all messages as read for an order (user side)
 */
export const markAllMessagesRead = async (commandeId) => {
    try {
        const response = await api.patch(`/commandes/${commandeId}/messages/read-all`);
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};

/**
 * Get total unread messages count across all orders
 */
export const getUnreadMessagesCount = async () => {
    try {
        const response = await api.get('/user/unread-messages-count');
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        return { unread_count: 0 };
    }
};

/**
 * Get user notifications
 */
export const getNotifications = async (page = 1) => {
    try {
        const response = await api.get(`/user/notifications?page=${page}`);
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        return { data: [], meta: { current_page: 1, last_page: 1, total: 0 }, unread_count: 0 };
    }
};

/**
 * Get unread notifications count
 */
export const getUnreadNotificationsCount = async () => {
    try {
        const response = await api.get('/user/notifications/unread-count');
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        return { unread_count: 0 };
    }
};

/**
 * Mark a single notification as read
 */
export const markNotificationAsRead = async (notificationId) => {
    try {
        const response = await api.patch(`/user/notifications/${notificationId}/read`);
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};

/**
 * Mark all notifications as read
 */
export const markAllNotificationsAsRead = async () => {
    try {
        const response = await api.patch('/user/notifications/read-all');
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};

export const getRecentOfferOrders = async () => {
    try {
        const response = await api.get('/user/recent-offer-orders');
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};

export const subscribePush = async (subscription) => {
    try {
        const response = await api.post('/user/push/subscribe', subscription);
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};

export const unsubscribePush = async (subscription) => {
    try {
        const response = await api.post('/user/push/unsubscribe', subscription);
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};

export const physicalProductService = {
    getCategories: async () => {
        const res = await api.get('/physical/categories');
        return res.data;
    },
    getSpecifications: async () => {
        const res = await api.get('/physical/specifications');
        return res.data;
    },
    getWilayas: async () => {
        const res = await api.get('/physical/wilayas');
        return res.data;
    },
    getCommunes: async (wilayaId) => {
        const res = await api.get('/physical/communes', { params: { wilaya_id: wilayaId } });
        return res.data;
    },
    getFeaturedProducts: async () => {
        const res = await api.get('/physical/featured');
        return res.data;
    },
    getProducts: async (params = {}) => {
        const res = await api.get('/physical/products', { params });
        return res.data;
    },
    getProductBySlug: async (slug) => {
        const res = await api.get(`/physical/products/${slug}`);
        return res.data;
    },
    placeOrder: async (data) => {
        const res = await api.post('/physical/order', data);
        return res.data;
    },
    getOrderByNumber: async (orderNumber) => {
        const res = await api.get(`/physical/orders/${orderNumber}`);
        return res.data;
    },
    getUserOrders: async () => {
        const res = await api.get('/user/physical-orders');
        return res.data;
    }
};

export const buybackService = {
    getProducts: async () => {
        const res = await api.get('/buyback/products');
        return res.data;
    },
    submitRequest: async (data) => {
        const res = await api.post('/buyback/request', data);
        return res.data;
    },
    getMyRequests: async () => {
        const res = await api.get('/buyback/my-requests');
        return res.data;
    },
    getRequestDetail: async (id) => {
        const res = await api.get(`/buyback/requests/${id}`);
        return res.data;
    },
    sendMessage: async (id, message) => {
        const res = await api.post(`/buyback/requests/${id}/messages`, { message });
        return res.data;
    }
};

export const announcementService = {
    getActiveAnnouncements: async () => {
        const res = await api.get('/announcements');
        return res.data;
    }
};

export const ratingService = {
    getRatings: async (type, id, params = {}) => {
        const res = await api.get(`/ratings/${type}/${id}`, { params });
        return res.data;
    },
    getTestimonials: async () => {
        const res = await api.get('/ratings/testimonials');
        return res.data;
    },
    submitRating: async (type, id, data) => {
        const res = await api.post(`/ratings/${type}/${id}`, data);
        return res.data;
    },
    deleteRating: async (id) => {
        const res = await api.delete(`/ratings/${id}`);
        return res.data;
    }
};

export const recommendationService = {
    getSubcategoryRecommendations: async (id) => {
        const res = await api.get(`/recommendations/subcategory/${id}`);
        return res.data;
    }
};

export { api };
export default api;